import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import client from "../../client";
import type { Ad, AdPositionEnum } from "../../client/api";
import type { AdsState } from "../constants";
import type { AppThunk, RootState } from "../index";
import { AdSensePositionEnum } from "../../config/adsenseConfig";

const initialState: AdsState = {
  value: {},
  status: "idle",
  error: null
};

const adsSlice = createSlice({
  name: "ads",
  initialState,
  reducers: {
    fetchRequest(state) {
      state.status = "loading";
      state.error = null;
    },
    fetchSuccess(
      state,
      action: PayloadAction<{ key: string; ads: Ad[] }>
    ) {
      state.status = "idle";
      state.error = null;
      state.value[action.payload.key] = action.payload.ads ?? [];
    },
    fetchError(state, action: PayloadAction<string>) {
      state.status = "failed";
      state.error = action.payload;
    },
    reset(state) {
      state.value = {};
      state.status = "idle";
      state.error = null;
    }
  }
});

const inFlightRequests = new Set<string>();

export const buildAdsKey = (position: AdPositionEnum | AdSensePositionEnum, aoi: number) =>
  `${position}-${aoi}`;

const getErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : "Unable to fetch ads";

export const fetchAdsByPosition =
  (position: AdPositionEnum, aoi: number): AppThunk<void> =>
    async (dispatch) => {
      const key = buildAdsKey(position, aoi);

      if (inFlightRequests.has(key)) {
        return;
      }

      inFlightRequests.add(key);
      dispatch(fetchRequest());

      try {
        const ads = await client().getAds(position, aoi);
        dispatch(fetchSuccess({ key, ads }));
      } catch (error) {
        dispatch(fetchError(getErrorMessage(error)));
      } finally {
        inFlightRequests.delete(key);
      }
    };

export const selectAds =
  (position: AdPositionEnum, aoi: number) =>
    (state: RootState): Ad[] => {
      const key = buildAdsKey(position, aoi);
      return state.ads.value[key] ?? [];
    };

export const { fetchRequest, fetchSuccess, fetchError, reset } = adsSlice.actions;
export const adsReducer = adsSlice.reducer;
